import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import EMOJI_DATA from '../utils/emojiData'

// -- Chat emoji popup ---------------------------------------------------
// Right-click popup for the lobby chat (MessagesPanel): a search box over
// two rows of emoji. Empty search shows recent picks, seeded with popular
// ones. Same look as the chat panel (see .chat-emoji-pop in styles.css).
// Rendered in a portal so the panel's overflow:hidden can't clip it.
//
// Props: x, y (client px of the right-click), onPick(emoji), onClose()

const COLS = 10
const SLOTS = COLS * 2                      // two rows
const RECENT_KEY = 'solcheddar:emojiRecent'
const POPULAR = ['😂', '🔥', '🚀', '💀', '😭', '👀', '💎', '🙌', '❤️', '👍',
  '😎', '🤝', '💯', '🤡', '🫡', '📈', '📉', '🐂', '🐻', '💰']

const NAME_OF = new Map(EMOJI_DATA.map(r => [r[0], r[1]]))
const ROWS = EMOJI_DATA.map(([emoji, name, tags]) => ({
  emoji, name, words: name.split(' '), tags: tags ? tags.split(' ') : [], hay: name + ' ' + tags,
}))

// Recent list is module-level so both panel instances share it.
let recent = (() => {
  try {
    const v = JSON.parse(localStorage.getItem(RECENT_KEY) || 'null')
    if (Array.isArray(v)) {
      const clean = v.filter(e => typeof e === 'string' && e && e.length <= 16).slice(0, SLOTS)
      if (clean.length) return clean
    }
  } catch {}
  return POPULAR.slice()
})()

export function pushRecentEmoji(emoji) {
  recent = [emoji, ...recent.filter(e => e !== emoji)].slice(0, SLOTS)
  try { localStorage.setItem(RECENT_KEY, JSON.stringify(recent)) } catch {}
}

// Best matches first: exact name, name prefix, a name word, a keyword, anywhere.
function searchEmoji(query) {
  const terms = query.toLowerCase().trim().split(/\s+/).filter(Boolean)
  if (!terms.length) return null
  const q = terms[0]
  const hits = []
  for (let i = 0; i < ROWS.length; i++) {
    const r = ROWS[i]
    if (!terms.every(t => r.hay.includes(t))) continue
    const score = r.name === q ? 0
      : r.name.startsWith(q) ? 1
      : r.words.some(w => w.startsWith(q)) ? 2
      : r.tags.some(w => w.startsWith(q)) ? 3 : 4
    hits.push([score, i])
  }
  hits.sort((a, b) => a[0] - b[0] || a[1] - b[1])
  return hits.slice(0, SLOTS).map(h => ROWS[h[1]].emoji)
}

export default function EmojiPicker({ x, y, onPick, onClose }) {
  const [query, setQuery] = useState('')
  const [pos, setPos] = useState({ left: x, top: y, ready: false })
  const boxRef = useRef(null)
  const searchRef = useRef(null)
  const closeRef = useRef(onClose)
  closeRef.current = onClose

  const list = useMemo(() => searchEmoji(query) || recent, [query])

  // Keep it on screen: open below-right of the cursor, flip up/left near edges.
  useLayoutEffect(() => {
    const el = boxRef.current
    if (!el) return
    const { width, height } = el.getBoundingClientRect()
    const pad = 6
    const vw = window.innerWidth, vh = window.innerHeight
    let left = x + width + pad > vw ? x - width : x
    let top = y + height + pad > vh ? y - height : y
    left = Math.max(pad, Math.min(left, vw - width - pad))
    top = Math.max(pad, Math.min(top, vh - height - pad))
    setPos({ left, top, ready: true })
  }, [x, y])

  useEffect(() => {
    const t = setTimeout(() => searchRef.current?.focus(), 0)
    const onDown = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) closeRef.current?.() }
    // Esc closes only the popup, not whatever panel sits under it.
    const onKey = (e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); closeRef.current?.() } }
    const onResize = () => closeRef.current?.()
    document.addEventListener('mousedown', onDown, true)
    document.addEventListener('keydown', onKey, true)
    window.addEventListener('resize', onResize)
    window.addEventListener('blur', onResize)
    return () => {
      clearTimeout(t)
      document.removeEventListener('mousedown', onDown, true)
      document.removeEventListener('keydown', onKey, true)
      window.removeEventListener('resize', onResize)
      window.removeEventListener('blur', onResize)
    }
  }, [])

  const pick = (emoji) => {
    if (!emoji) return
    pushRecentEmoji(emoji)
    onPick?.(emoji)
  }

  const cells = []
  for (let i = 0; i < SLOTS; i++) {
    const e = list[i]
    cells.push(e
      ? <button key={i} type="button" className="chat-emoji-cell" title={NAME_OF.get(e) || e} onClick={() => pick(e)}>{e}</button>
      : <span key={i} className="chat-emoji-cell is-empty" />)
  }

  return createPortal(
    <div
      ref={boxRef}
      className="chat-emoji-pop"
      style={{ left: pos.left, top: pos.top, visibility: pos.ready ? 'visible' : 'hidden' }}
      onContextMenu={e => e.preventDefault()}
    >
      <input
        ref={searchRef}
        className="chat-input chat-emoji-search"
        placeholder="search emoji"
        value={query}
        maxLength={40}
        onChange={e => setQuery(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') { e.preventDefault(); pick(list[0]) }
        }}
      />
      <div className="chat-emoji-grid">
        {cells}
        {list.length === 0 && <div className="chat-emoji-none">{query.trim() ? 'no match' : ''}</div>}
      </div>
    </div>,
    document.body,
  )
}
